import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PayoutBankAccountDto } from '@paladin-monorepo/common';
import { PayoutBankAccountVerificationStatus, isValidBic, isValidIban } from '@paladin-monorepo/common';
import { payoutBankAccountsApi } from '@/lib/api/payoutBankAccounts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const statusVariant = (
  s: PayoutBankAccountDto['verificationStatus'],
): 'default' | 'secondary' | 'destructive' | 'outline' => {
  if (s === PayoutBankAccountVerificationStatus.VERIFIED) return 'default';
  if (s === PayoutBankAccountVerificationStatus.REJECTED) return 'destructive';
  return 'secondary';
};

export function PayoutBankAccountsSection({
  accountId,
  accounts,
  defaultHolderName,
}: {
  accountId: string;
  accounts: PayoutBankAccountDto[];
  defaultHolderName: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['billing-settings', accountId] });
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PayoutBankAccountDto | null>(null);
  const [holder, setHolder] = useState('');
  const [iban, setIban] = useState('');
  const [bic, setBic] = useState('');

  const openCreate = () => {
    setEditing(null);
    setHolder(defaultHolderName || '');
    setIban('');
    setBic('');
    setDialogOpen(true);
  };

  const openEdit = (a: PayoutBankAccountDto) => {
    setEditing(a);
    setHolder(a.accountHolderName);
    setIban(a.iban);
    setBic(a.bic ?? '');
    setDialogOpen(true);
  };

  const createMut = useMutation({
    mutationFn: () =>
      payoutBankAccountsApi.create(accountId, {
        accountHolderName: holder.trim(),
        iban,
        bic,
        isPrimary: accounts.length === 0,
      }),
    onSuccess: () => {
      invalidate();
      toast.success(t('billing.payoutAccounts.saved'));
      setDialogOpen(false);
    },
    onError: () => toast.error(t('billing.payoutAccounts.error')),
  });

  const updateMut = useMutation({
    mutationFn: () =>
      payoutBankAccountsApi.update(accountId, editing!.id, {
        accountHolderName: holder.trim(),
        iban,
        bic,
      }),
    onSuccess: () => {
      invalidate();
      toast.success(t('billing.payoutAccounts.saved'));
      setDialogOpen(false);
    },
    onError: () => toast.error(t('billing.payoutAccounts.error')),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => payoutBankAccountsApi.remove(accountId, id),
    onSuccess: () => {
      invalidate();
      toast.success(t('billing.payoutAccounts.deleted'));
    },
    onError: () => toast.error(t('billing.payoutAccounts.error')),
  });

  const primaryMut = useMutation({
    mutationFn: (id: string) => payoutBankAccountsApi.setPrimary(accountId, id),
    onSuccess: () => {
      invalidate();
      toast.success(t('billing.payoutAccounts.primarySet'));
    },
    onError: () => toast.error(t('billing.payoutAccounts.error')),
  });

  const validateForm = (): boolean => {
    if (!holder.trim()) {
      toast.error(t('billing.payoutAccounts.holderRequired'));
      return false;
    }
    if (!isValidIban(iban)) {
      toast.error(t('billing.payoutAccounts.invalidIban'));
      return false;
    }
    if (!isValidBic(bic)) {
      toast.error(t('billing.payoutAccounts.invalidBic'));
      return false;
    }
    return true;
  };

  const submit = () => {
    if (!validateForm()) return;
    if (editing) updateMut.mutate();
    else createMut.mutate();
  };

  const busy = createMut.isPending || updateMut.isPending;

  return (
    <div className="ml-6 max-w-2xl space-y-4 rounded-md border bg-muted/30 p-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium">{t('billing.payoutAccounts.title')}</p>
          <p className="text-xs text-muted-foreground">{t('billing.payoutAccounts.hint')}</p>
        </div>
        <Button type="button" size="sm" variant="secondary" onClick={openCreate}>
          {t('billing.payoutAccounts.add')}
        </Button>
      </div>

      {accounts.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('billing.payoutAccounts.empty')}</p>
      ) : (
        <ul className="space-y-3">
          {accounts.map((a) => (
            <li
              key={a.id}
              className="flex flex-col gap-2 rounded-md border bg-background p-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="space-y-1 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{a.accountHolderName}</span>
                  {a.isPrimary && (
                    <Badge variant="outline">{t('billing.payoutAccounts.primary')}</Badge>
                  )}
                  <Badge variant={statusVariant(a.verificationStatus)}>
                    {t(`billing.payoutAccounts.status.${a.verificationStatus}`)}
                  </Badge>
                </div>
                <p className="font-mono text-xs text-muted-foreground">{a.iban}</p>
                {a.bic && <p className="font-mono text-xs text-muted-foreground">BIC: {a.bic}</p>}
                {a.rejectionReason && (
                  <p className="text-xs text-destructive">{a.rejectionReason}</p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {!a.isPrimary && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => primaryMut.mutate(a.id)}
                    disabled={primaryMut.isPending}
                  >
                    {t('billing.payoutAccounts.makePrimary')}
                  </Button>
                )}
                <Button type="button" size="sm" variant="outline" onClick={() => openEdit(a)}>
                  {t('billing.payoutAccounts.edit')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="text-destructive"
                  onClick={() => {
                    if (window.confirm(t('billing.payoutAccounts.confirmDelete'))) {
                      deleteMut.mutate(a.id);
                    }
                  }}
                  disabled={deleteMut.isPending}
                >
                  {t('billing.payoutAccounts.delete')}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editing ? t('billing.payoutAccounts.editTitle') : t('billing.payoutAccounts.addTitle')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>{t('billing.payoutAccounts.holder')}</Label>
              <Input value={holder} onChange={(e) => setHolder(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>IBAN</Label>
              <Input value={iban} onChange={(e) => setIban(e.target.value)} className="font-mono" />
            </div>
            <div className="space-y-1">
              <Label>{t('billing.payoutAccounts.bic')}</Label>
              <Input value={bic} onChange={(e) => setBic(e.target.value)} className="font-mono" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              {t('billing.cancel')}
            </Button>
            <Button type="button" onClick={submit} disabled={busy}>
              {busy ? t('billing.saving') : t('billing.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
